import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';

const MILESTONES = [
    {
        count: 1,
        headline: "You've published your first article!",
        body: "You're officially a content creator. The journey of 1,000 articles begins with one.",
        emoji: '🎉',
        tier: null,
    },
    {
        count: 10,
        headline: '10 articles published!',
        body: "You're in the top 40% of ContentVertex creators. Most people quit before reaching 10.",
        emoji: '🔥',
        tier: null,
    },
    {
        count: 25,
        headline: '25 articles published.',
        body: "You've unlocked Pro Creator status. You now have more published articles than 80% of content creators ever will.",
        emoji: '★',
        tier: 'pro',
    },
    {
        count: 50,
        headline: '50 articles!',
        body: "You're beating 85% of content creators. Your content library is compounding — every article makes the next one more valuable.",
        emoji: '⚡',
        tier: 'elite',
    },
    {
        count: 100,
        headline: '100 articles.',
        body: "You are a ContentVertex legend. Only 3% of creators reach this milestone. You are one of them.",
        emoji: '👑',
        tier: 'elite',
    },
];

const STORAGE_KEY = 'cv_last_celebrated_count';

// Returns the milestone to celebrate, or null if none pending
export function getMilestoneToCelebrate(totalArticles) {
    const lastCelebrated = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    const pending = MILESTONES.filter(
        m => m.count <= totalArticles && m.count > lastCelebrated
    );
    return pending.length > 0 ? pending[pending.length - 1] : null;
}

const ConfettiPiece = ({ style }) => (
    <div
        className="absolute w-2 h-2 rounded-sm opacity-80 animate-bounce"
        style={style}
    />
);

const MilestoneCelebration = ({ milestone, onDismiss }) => {
    const [confetti] = useState(() =>
        Array.from({ length: 30 }, (_, i) => ({
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 60}%`,
            background: ['#8b5cf6', '#3b82f6', '#f59e0b', '#10b981', '#ec4899'][i % 5],
            animationDelay: `${Math.random() * 1}s`,
            animationDuration: `${0.8 + Math.random() * 0.6}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
        }))
    );

    useEffect(() => {
        // Record that we celebrated this milestone
        localStorage.setItem(STORAGE_KEY, String(milestone.count));
    }, [milestone.count]);

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm">
            {/* Confetti */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {confetti.map((style, i) => (
                    <ConfettiPiece key={i} style={style} />
                ))}
            </div>

            <div className="relative w-full max-w-md mx-4 bg-zinc-900 border border-white/10 rounded-2xl p-8 text-center shadow-2xl">
                <div className="text-5xl mb-4">{milestone.emoji}</div>
                <h2 className="text-2xl font-bold text-zinc-100 mb-2">{milestone.headline}</h2>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">{milestone.body}</p>

                {milestone.tier && (
                    <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/30 text-violet-300 text-sm font-semibold">
                        {milestone.tier === 'elite' ? '⚡ Elite Creator Unlocked' : '★ Pro Creator Unlocked'}
                    </div>
                )}

                <Button
                    onClick={onDismiss}
                    className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white border-0"
                >
                    Keep the streak going
                </Button>
            </div>
        </div>
    );
};

export default MilestoneCelebration;
